<template>
  <b-row class="referrals-dashboard" v-if="dashboard">
    <b-col cols="12" md="8">

      <b-row class="welcome-block bg-primary-a1 my-3 p-3">
        <h1>
          <strong class="d-block mb-2">
            <template v-if="isRecruiterAdmin">{{ $t('dashboard.welcome-greeting.welcome-back', { userFirstName: user.firstName }) }}</template>
            <template v-else>{{ $t('dashboard.welcome-greeting.hey-there', { userFirstName: user.firstName }) }}</template>
          </strong>

          <template v-if="dashboard && !isRecruiter && dashboard.referralConnections && dashboard.referralConnections.total">
            <i18n tag="span" class="d-block" :path="dashboard.referralConnections.total === 1
              ? 'dashboard.welcome-message.employee-referrals-one'
              : 'dashboard.welcome-message.employee-referrals-many'">
                <template v-slot:current-openings-link>
                  <router-link :to="{ name: 'ReferralRequests' }">{{ $t('dashboard.welcome-message.referral-requests-link', { n: dashboard.referralConnections.total }) }}</router-link>
                </template>
            </i18n>
          </template>

          <template v-if="dashboard && dashboard.totalActiveJobs && dashboard.totalActiveJobs.total">
            <template v-if="isRecruiter">
              <i18n tag="span" class="d-block" :path="dashboard.totalActiveJobs.total === 1
                ? 'dashboard.welcome-message.recruiter-jobs-one'
                : 'dashboard.welcome-message.recruiter-jobs-many'">
                  <template v-slot:current-openings-link>
                    <router-link :to="{ name: 'JobIndex' }">{{ $t('dashboard.welcome-message.current-openings-link', { n: dashboard.totalActiveJobs.total }) }}</router-link>
                  </template>
              </i18n>
            </template>
            <template v-else>
              <i18n tag="span" class="d-block" :path="dashboard.totalActiveJobs.total === 1
                ? 'dashboard.welcome-message.employee-jobs-one'
                : 'dashboard.welcome-message.employee-jobs-many'">
                  <template v-slot:current-openings-link>
                    <router-link :to="{ name: 'JobIndex' }">{{ $t('dashboard.welcome-message.current-openings-link', { n: dashboard.totalActiveJobs.total }) }}</router-link>
                  </template>
              </i18n>
            </template>
          </template>
        </h1>
      </b-row>

      <div v-if="company && company.siteBranding && company.siteBranding.headerImage" class="my-3">
        <a v-if="company.handle === 'king'" href="https://kingfluence.com/pages/viewpage.action?pageId=328610733" rel="external nofollow" target="_blank">
          <b-img fluid :src="company.siteBranding.headerImage.fileLocation" />
        </a>
        <b-img v-else fluid :src="company.siteBranding.headerImage.fileLocation"/>
      </div>

      <welcome-card v-for="(card, i) in dashboardCards" :key="i"
        class="my-3 d-block d-md-none" :card="card"/>

      <welcome-card v-if="dashboard && dashboard.dashboardCard" class="my-3 d-block d-md-none" :card="dashboard.dashboardCard"/>
      <b-btn class="my-3 d-block d-md-none" v-if="!isRecruiter && hasUploadedContacts !== true && company.sitePreferences.canSyncContacts"
        :to="{ name: 'ProfileSyncContacts' }" variant="primary">{{ $t('ctas.sync-your-connections') }}</b-btn>
      <rewards-card v-if="!isRecruiter && isRewardsEnabled"
        :balance="dashboard.rewardTokensBalance" :rewards="dashboard.rewards" :total="dashboard.rewardTokensTotal" class="my-3 d-block d-md-none"/>

      <template v-if="Array.isArray(leaderboards) && leaderboards.length">
        <leaderboard class="mt-3 mb-3 d-block d-md-none" v-for="leaderboard in leaderboards" :key="leaderboard.id" :leaderboard="leaderboard" />
      </template>

      <template v-if="!isRecruiter && dashboard.referralConnections && dashboard.referralConnections.edges && dashboard.referralConnections.edges.length">
        <div class="my-3">
          <h2 v-if="isSainsburys">Intro Requests</h2>
          <h2 v-else>Referral Requests</h2>
          <p v-if="isSainsburys">You have been have asked if you will introduce the following candidates:</p>
          <p v-else>You have been have asked if you will refer the following candidates:</p>
          <job-referrals-container :results="dashboard.referralConnections" @finished="finishedReferralRequest"/>
          <template v-if="(dashboard.referralConnections.total > 3) && !isRecruiter">
            <p class="text-right"><router-link :to="{ name: 'ReferralRequests' }">View More ({{dashboard.referralConnections.total-3}})</router-link></p>
          </template>
        </div>
      </template>

      <hr class="d-block d-md-none"/>

      <div class="welcome-block my-3 pl-1">
        <h1>{{ $t('dashboard.roles-title') }}</h1>
        <p>{{ $t('dashboard.roles-description-nosync') }}</p>
      </div>

      <div class="my-3">
        <jobs-filter layout="block" :loading="$apollo.queries.jobs.loading || !jobs" :results="jobs" :hide-pagination="true"
          :where="filterJobsWhere" @whereUpdated="filterJobsWhereUpdated" :whereCustomFields="dashboard.jobAggs"
          :order="filterJobsOrder" @orderUpdated="filterJobsOrderUpdated"/>
        <div class="d-flex justify-content-center">
          <b-btn :to="{ name: 'JobIndex', query: hasMoreJobsFilter ? { filter: hasMoreJobsFilter } : undefined }"
            variant="outline">{{ $t('jobs.ctas.view-all-jobs') }}</b-btn>
        </div>
      </div>

    </b-col>
    <b-col cols="12" md="4">
      <welcome-card v-for="(card, i) in dashboardCards" :key="i"
        class="my-3 d-none d-md-block" :card="card"/>

      <!-- <onboarding-checklist class="mb-3" v-if="dashboard && dashboard.onboarding" :options="dashboard.onboarding"/> -->

      <template v-if="isRecruiter && company.sitePreferences.canSyncContacts">
        <b-card class="my-3" v-if="$apollo.queries.stats.loading">Loading stats..</b-card>
        <stats class="my-3" v-else can-expand :value="stats" />
      </template>

      <rewards-card v-if="!isRecruiter && isRewardsEnabled && dashboard && Array.isArray(dashboard.rewards)"
        class="my-3 d-none d-md-block"
        :rewards="dashboard.rewards" :claimed="dashboard.claimedRewards"
        :balance="dashboard.rewardTokensBalance" :total="dashboard.rewardTokensTotal"/>

      <template v-if="Array.isArray(leaderboards) && leaderboards.length">
        <leaderboard class="my-3 d-none d-md-block" v-for="leaderboard in leaderboards" :key="leaderboard.id" :leaderboard="leaderboard" data-tour="Leaderboard" />
      </template>

      <csr-initiative v-if="isVoltaTrucks" class="my-3 d-none d-md-block"
        :monthlyTotalPoints="dashboard.leaderboardStatsInPeriod ? dashboard.leaderboardStatsInPeriod.totalEvents : 0"
        :leaderboardMonthlyPoints="dashboard.leaderboardStatsInPeriodMonthly ? dashboard.leaderboardStatsInPeriodMonthly.map(({ totalEvents }) => totalEvents) : [0]"/>

      <!-- <BupaDentalPracticeAreaLeaderboard v-if="isBupaDental"
        :loading="$apollo.queries.success_getPracticeAreaLeaderboard.loading"
        :results="success_getPracticeAreaLeaderboard"/> -->
    </b-col>
  </b-row>
</template>

<script>
import base64 from 'base-64';
import ms from 'ms';
import enumsMixin from '@src/mixins/enums';
import JobsFilter from '@/components/jobs/FilterContainer';
// import OnboardingChecklist from '@/components/dashboard/onboarding-checklist';
import Stats from '@/components/dashboard/stats';
import WelcomeCard from '@/components/dashboard/Welcome-Card';
import DashboardWelcome from '@/components/dashboard/welcome';
import Leaderboard from '@/components/dashboard/dashboard-leaderboard';
import CsrInitiative from '@/components/dashboard/CsrInitiative';
import JobReferralsContainer from '@/components/job-referrals/container';
import RewardsCard from '@/components/rewards/Dashboard';
// import ShareWithColleaguesModal from '@/modals/share-with-colleagues';
import { mapRoot } from '@src/mixins/root';
// import BupaDentalPracticeAreaLeaderboard from '@/components/success/bupadental-practiceAreaLeaderboard';
import DashboardRecruiterQuery from '@/graphql/queries/DashboardRecruiter.gql';
import DashboardEmployeeQuery from '@/graphql/queries/DashboardEmployee.gql';
import DashboardStatsQuery from '@/graphql/queries/DashboardStats.gql';
// import SuccessFunctionQuery from '@/graphql/queries/SuccessFunction.gql';
import JobsIndexQuery from '@/graphql/queries/JobsIndex.gql';

const sessionStorage = 'sessionStorage' in global && global.sessionStorage ? global.sessionStorage : null;

export default {
  name: 'ReferralsDashboard',
  components: {
    JobsFilter,
    // OnboardingChecklist,
    DashboardWelcome,
    WelcomeCard,
    Stats,
    JobReferralsContainer,
    Leaderboard,
    // ShareWithColleaguesModal,
    // BupaDentalPracticeAreaLeaderboard,
    RewardsCard,
    CsrInitiative,
  },
  mixins: [
    enumsMixin,
  ],
  data: () => ({
    referrals: null,
    filterJobsWhere: {
      // matches: { gt: 0 },
      status: 'ACTIVE',
    },
    filterJobsOrder: 'CREATED_DESC',
    // filterMatchesRelevance: 1.0,
    hasMoreJobsFilter: null,
  }),
  created() {
    // const { STRONG } = this.contactRelevanceBoundaries;
    // this.filterMatchesRelevance = STRONG;
    if (this.company && this.company.sitePreferences) {
      const { company: { sitePreferences } } = this;
      this.filterJobsOrder = (this.isRecruiter ? sitePreferences.defaultRecruiterJobOrderBy
        : sitePreferences.defaultJobOrderBy) || this.filterJobsOrder;
    }
  },
  apollo: {
    dashboard: {
      query() {
        return this.isRecruiter ? DashboardRecruiterQuery : DashboardEmployeeQuery;
      },
      variables() {
        const beginningOfPeriod = new Date(2022, 3, 1);
        return this.isRecruiter ? {
          leaderboardStatsWhere: {
            after: beginningOfPeriod.toISOString(),
            eventName_in: [ 'JOB_SHARED', 'REFERRAL_SHARED' ],
          }
        } : {
          isReferrals: this.isReferralsFeatureEnabled === true,
          isInternalMobility: this.isInternalMobilityEnabled === true,
          leaderboardStatsWhere: {
            after: beginningOfPeriod.toISOString(),
          },
        };
      },
      update({ company, user }) {
        /* eslint-disable object-property-newline */
        if (this.isRecruiter) {
          const {
            matchesCount, sharePreferences, shareTemplates, jobAggs,
            totalActiveJobs, dashboardCards,
            claimedRewardsToVerify, claimedRewardsToApprove, claimedRewardsToFulfil,
            leaderboardStatsInPeriodMonthly
          } = company;
          const { leaderboards } = user;
          const leaderboardStatsInPeriod = leaderboardStatsInPeriodMonthly[leaderboardStatsInPeriodMonthly.length - 1];
          console.log({ leaderboardStatsInPeriodMonthly });
          return {
            matchesCount, sharePreferences, shareTemplates, jobAggs,
            totalActiveJobs, dashboardCards,
            claimedRewardsToVerify, claimedRewardsToApprove, claimedRewardsToFulfil,
            leaderboards, leaderboardStatsInPeriod, leaderboardStatsInPeriodMonthly
          };
        } else {
          const {
            jobAggs, sharePreferences, shareTemplates,
            totalActiveJobs, dashboardCards, rewards,
            leaderboardStatsInPeriodMonthly
          } = company;
          const {
            completion, matchesCount, leaderboards, referralConnections,
            rewardTokensBalance, claimedRewards,
          } = user;
          const leaderboardStatsInPeriod = leaderboardStatsInPeriodMonthly[leaderboardStatsInPeriodMonthly.length - 1];
          return {
            jobAggs, sharePreferences, shareTemplates,
            totalActiveJobs, dashboardCards, rewards,
            completion, matchesCount, leaderboards, referralConnections,
            rewardTokensBalance, claimedRewards, leaderboardStatsInPeriod,
            leaderboardStatsInPeriodMonthly
          };
        }
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load the dashboard' });
      },
      skip() {
        return !this.isLoggedIn;
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
    jobs: {
      query: JobsIndexQuery,
      variables() {
        const { filterJobsWhere: where, filterJobsOrder: order } = this;
        return { isEmployee: !this.isRecruiter, where, order, page: 1, limit: 6 };
      },
      update(data) {
        if (this.isRecruiter) {
          const { company: { jobConnections } } = data;
          return jobConnections;
        } else {
          const { user: { jobConnections } } = data;
          return jobConnections;
        }
      },
      error(err) {
        this.$log.error(err);
      },
      skip() {
        return !this.isLoggedIn;
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      deep: true,
      throttle: ms('1s'),
    },
    stats: {
      query: DashboardStatsQuery,
      update: ({ company: { totalStats } }) => totalStats,
      error(err) {
        this.err = `${err}`;
      },
      skip() {
        return !this.isRecruiter;
      },
      fetchPolicy: 'network-only',
    },
    // success_getPracticeAreaLeaderboard: {
    //   query: SuccessFunctionQuery,
    //   variables: { name: 'bupadental-getPracticeAreaLeaderboard' },
    //   update({ company }) {
    //     const { successFunction } = company || {};
    //     return successFunction || {};
    //   },
    //   error(err) {
    //     this.$log.error(err);
    //   },
    //   skip() {
    //     return !(this.isLoggedIn && this.isBupaDental);
    //   },
    //   errorPolicy: 'all',
    //   fetchPolicy: 'network-only',
    // },
  },
  metaInfo() {
    return {
      title: this.$t('navigation.dashboard'),
    };
  },
  computed: {
    ...mapRoot([
      'company', 'user',
      'isLoggedIn', 'isRecruiter', 'isRecruiterAdmin', 'userSegmentIDs',
      'isInternalMobilityEnabled', 'isReferralsFeatureEnabled',
      'isBupaDental', 'isSainsburys', 'isVoltaTrucks'
    ]),
    leaderboards() {
      if (this.company && this.company.sitePreferences && this.company.sitePreferences.useGamification) {
        const filterActiveDates = ({ startDate, endDate }) => (!startDate || startDate < Date.now()) && (!endDate || endDate > Date.now());
        if (this.isRecruiter) {
          return (Array.isArray(this.dashboard.leaderboards) ? this.dashboard.leaderboards : []).filter(filterActiveDates);
        } else {
          let leaderboards = (Array.isArray(this.dashboard.leaderboards) ? this.dashboard.leaderboards : []).filter(filterActiveDates);
          if (this.company.handle === 'mckesson') {
            leaderboards = leaderboards.filter(({ type }) => type === 'INDIVIDUALS');
          }
          return leaderboards;
        }
      }
      return null;
    },
    hasUploadedContacts() {
      if (sessionStorage.getItem('lastUploadedAt')) {
        const lastUploadedAt = new Date(sessionStorage.getItem('lastUploadedAt'));
        if (Number.isNaN(lastUploadedAt.getTime())) {
          this.$log.debug('Clearing lastUploadedAt since isNaN');
          sessionStorage.removeItem('lastUploadedAt');
          return null;
        }
        if ((lastUploadedAt.getTime() + ms('10m')) < Date.now()) {
          this.$log.debug('Clearing lastUploadedAt since older than 10m');
          sessionStorage.removeItem('lastUploadedAt');
          return null;
        }
        this.$log.debug('Contacts lastUploadedAt within last 10m');
        return true;
      }
      return null;
    },
    dashboardCards() {
      const { dashboardCards } = this.dashboard || {};
      const { locale } = this.$i18n;

      return (Array.isArray(dashboardCards) ? dashboardCards : []).filter(card => {
        const cardSegmentIDs = Array.isArray(card.segments) ? card.segments.map(({ id }) => id) : [];

        const tests = [
          // Card locale isn't set, or matches the user's locale
          !card.locale || card.locale === locale,
          // Card doesn't have any segments, or the user is in one of the segments for this card
          !cardSegmentIDs.length || cardSegmentIDs.find(id => this.userSegmentIDs.includes(id)),
        ];

        return tests.reduce((a, b) => a && b, true);
      });
    },
    isRewardsEnabled() {
      const { isRewardsEnabled } = (this.company || {}).sitePreferences || {};
      const { rewards } = this.dashboard || {};
      return isRewardsEnabled === true && Array.isArray(rewards) && rewards.length;
    },
  },
  methods: {
    filterJobsWhereUpdated(where) {
      this.filterJobsWhere = where;
      this.hasMoreJobsFilter = base64.encode(JSON.stringify({
        where: this.filterJobsWhere,
        order: this.filterJobsOrder,
      }));
      this.$scrollTo('.jobs-filter-container');
    },
    filterJobsOrderUpdated(order) {
      this.filterJobsOrder = order;
      this.hasMoreJobsFilter = base64.encode(JSON.stringify({
        where: this.filterJobsWhere,
        order: this.filterJobsOrder,
      }));
      this.$scrollTo('.jobs-filter-container');
    },
    finishedReferralRequest({ referralID }) {
      try {
        const client = this.$apollo.getClient();
        // Fetch query & variables from the Apollo definition above, D-R-Y!
        const query = this.$options.apollo.dashboard.query.call(this);
        const variables = this.$options.apollo.dashboard.variables.call(this);
        const data = client.readQuery({ query, variables });
        if (data && data.user && data.user.referralConnections) {
          data.user.referralConnections.edges = data.user.referralConnections.edges.filter(({ entry: { id } }) => id !== referralID);
          data.user.referralConnections.total--;
        }
        client.writeQuery({ query, variables, data });
      } catch (err) {
        this.$log.error(err);
      }
    },
  },
};
</script>

<style lang="scss">
.referrals-dashboard {
  h1 {
    font-size: 24px;
    font-weight: bold;
  }
  .upload-button {
    text-align: center;
    border-radius: 4px;
    &:hover {
      opacity: 0.8;
      cursor: pointer;
    }
    h3 {
      display: inline-block;
    }
    p {
      margin-block-start: 0;
      margin-block-end: 0;
    }
    &.card {
      padding: 0.5em;
      .card-body {
        padding: 0.5em;
      }
    }
  }
  .welcome-block {
    h1 {
      line-height: 1.28;
      font-weight: 400;
    }
    &.row {
      margin-left: 0px;
      margin-right: 0px
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Dashboards/Referrals.vue